import { coreApi } from './calls';
import dayjs from 'dayjs';

/**
 * Client inform API module
 * Uses unified backend endpoints with type parameter
 * Backend: PartnerClientInformController
 */
export const clientInformApi = {
  /**
   * Preview email before sending
   * @param {Object} payload - Request payload with type and building_units
   * @returns {Promise} Response with HTML preview
   */
  preview: (payload) => coreApi.post('/partners/inform-client/preview', payload),

  /**
   * Send client notification emails
   * @param {Object} payload - Request payload with type and building_units
   * @returns {Promise} Response with success status
   */
  send: (payload) => coreApi.post('/partners/inform-client', payload),

  /**
   * Resolve who would actually be mailed, per building unit.
   * Backed by the same resolver as preview/send, so it never over-reports.
   * @param {Object} payload - { building_units: number[] }
   * @returns {Promise} Response with { by_unit, buildings_without_contacts }
   */
  recipients: (payload) => coreApi.post('/partners/inform-client/recipients', payload),
};

/**
 * Table rows carry the schedule row id in `id`; the building unit id lives
 * separately. Every caller must resolve it the same way as the send payload does.
 * @param {Object} unit - selected table row
 * @returns {number|undefined} building unit id
 */
export const getBuildingUnitId = (unit) =>
  unit?.building_unit_id || unit?.building_unit?.id;

/**
 * Build payload for API requests
 * @param {string} type - Modal type: 'replacement', 'switch', 'change', 'rescheduling'
 * @param {Array} selectedUnits - Selected building units from table
 * @param {Object} formValues - Form values from modal
 * @returns {Object} API payload
 */
export const buildClientInformPayload = (type, selectedUnits, formValues) => {
  if (!selectedUnits || selectedUnits.length === 0) {
    throw new Error('No building units selected');
  }

  // Extract building unit IDs
  const buildingUnitIds = selectedUnits.map(unit => 
    unit.building_unit_id || unit.id
  );
  
  // Get partner_id from first selected unit
  const partnerId = selectedUnits[0]?.partner_id 
    || selectedUnits[0]?.service_rrules?.at(-1)?.partner_id;

  if (!partnerId) {
    throw new Error('Partner ID not found in selected units');
  }

  const payload = {
    type,
    building_units: buildingUnitIds,
    partner_id: partnerId,
    manager_employee_id: formValues.manager_employee_id || null,
  };

  // Add type-specific fields
  if (type === 'replacement' || type === 'switch') {
    payload.partner = formValues.substitute_partner_id || formValues.partner;
    payload.days = formValues.days; // e.g. "pondělí, úterý"
  }
  
  if (type === 'change') {
    payload.days = formValues.days;
  }
  
  if (type === 'rescheduling') {
    payload.date = formValues.compensation_date; // e.g. "01.01.2027"
  }

  // Week range is common for all types
  if (formValues.week) {
    payload.week = formValues.week; // e.g. "29.06.2026 - 05.07.2026"
  }

  return payload;
};

/**
 * Group table rows so a single API call never mixes rows whose notification
 * content (partner/week/days) actually differs - the backend renders one shared
 * email body per call, so rows with a different signature must be sent separately.
 * @param {Array} rows - table rows to group
 * @param {Function} signatureFn - (row) => string, rows sharing a signature are batched together
 * @returns {Array<Array>} array of row groups, in first-seen order
 */
export const groupRowsBySignature = (rows, signatureFn) => {
  const groups = new Map();
  rows.forEach((row) => {
    const key = signatureFn(row);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });
  return Array.from(groups.values());
};

/**
 * Format date range for API (DatePicker values to string)
 * @param {Array} dates - Array of dayjs objects [startDate, endDate]
 * @returns {string} Formatted date range "DD.MM.YYYY - DD.MM.YYYY"
 */
export const formatDateRangeForApi = (dates) => {
  if (!dates || dates.length !== 2) return null;
  
  const [start, end] = dates;
  const startFormatted = dayjs(start).format('DD.MM.YYYY');
  const endFormatted = dayjs(end).format('DD.MM.YYYY');
  
  return `${startFormatted} - ${endFormatted}`;
};

/**
 * Format single date for API (DatePicker value to string)
 * @param {Object} date - Dayjs object
 * @returns {string} Formatted date "DD.MM.YYYY"
 */
export const formatDateForApi = (date) => {
  if (!date) return null;
  return dayjs(date).format('DD.MM.YYYY');
};

/**
 * Format several dates for API (DatePicker values to string)
 * Empty slots are skipped, dates are sorted chronologically
 * @param {Array} dates - Array of dayjs/moment objects (may contain nulls)
 * @returns {string|null} Formatted dates "DD.MM.YYYY, DD.MM.YYYY"
 */
export const formatDatesForApi = (dates) => {
  const filled = (dates || []).filter(Boolean);
  if (!filled.length) return null;

  return filled
    .map(date => dayjs(date))
    .sort((a, b) => a.valueOf() - b.valueOf())
    .map(date => date.format('DD.MM.YYYY'))
    .join(', ');
};

/**
 * Format cleaning days from rrule for display
 * @param {string} rrule - RRULE string
 * @returns {string} Formatted days in Czech
 */
export const formatRruleDays = (rrule) => {
  if (!rrule) return '';
  
  const daysMap = {
    'MO': 'pondělí',
    'TU': 'úterý',
    'WE': 'středa',
    'TH': 'čtvrtek',
    'FR': 'pátek',
    'SA': 'sobota',
    'SU': 'neděle',
  };
  
  // Extract BYDAY from rrule
  const byDayMatch = rrule.match(/BYDAY=([^;]+)/);
  if (!byDayMatch) return '';
  
  const days = byDayMatch[1].split(',');
  return days.map(day => daysMap[day.trim()] || day).join(', ');
};

/**
 * Get substitute partner name from building unit
 * @param {Object} unit - Building unit object
 * @returns {string} Partner full name
 */
export const getSubstitutePartnerName = (unit) => {
  const substitutePartner = unit.substitute_partner;
  if (!substitutePartner) return '';
  
  return `${substitutePartner.first_name || ''} ${substitutePartner.last_name || ''}`.trim();
};

/**
 * Get partner name from building unit
 * @param {Object} unit - Building unit object
 * @returns {string} Partner full name
 */
export const getPartnerName = (unit) => {
  const partner = unit.partner || unit.service_rrules?.at(-1)?.partner;
  if (!partner) return '';
  
  return `${partner.first_name || ''} ${partner.last_name || ''}`.trim();
};

/**
 * Get building address for display
 * @param {Object} unit - Building unit object
 * @returns {string} Full address
 */
export const getBuildingAddress = (unit) => {
  const buildingUnit = unit.building_unit || unit;
  if (!buildingUnit) return '';
  
  const street = buildingUnit.street || '';
  const houseNumber = buildingUnit.house_number || '';
  
  return `${street} ${houseNumber}`.trim();
};
